import { createActionsColumn } from '@app/modules/audit/components/create-actions-column';
import { createStatusColumn } from '@app/modules/audit/components/create-status-column';
import { parseRangeFromTs } from '@mi-front/core';
import { Chip, MrtTable, ResultsCount, ViewLoader } from '@mi-front/ui';
import { FormattedMessage } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { LTime } from '../../../../../../components/date/LTime';
import { useFocus } from '../../../../../logger/screens/stream/stores/stream-screen.store';
import { useAuditMentionsMatches } from '../../hooks/useAuditMentionsMatches';
import { AuditLiveMention } from '../../types';

import { Station } from '@app/core/types/stations';
import { MentionConfidenceControls } from '@app/modules/logger/screens/stream/components/audit/focus-markers/mentions/components/MentionConfidenceControls';
import { MentionPlayBtn } from '@app/modules/audit/modules/station-audit/components/mentions/MentionPlayBtn';
import { MentionShowButton } from '@app/modules/audit/modules/station-audit/components/mentions/MentionShowButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton } from '@mui/material';
import { useIntl } from 'react-intl';

interface Props {
  readonly station: Station;
}

export function WaveMentionsStatusView(p: Props) {
  const { $t } = useIntl();
  const focus = useFocus();
  const [debouncedFocus] = useDebounce(focus, 300);
  const mentionsQuery = useAuditMentionsMatches(p.station, parseRangeFromTs(debouncedFocus));

  const mentions = mentionsQuery.data;

  if (mentionsQuery.isLoading) {
    return <ViewLoader />;
  }

  return mentions && mentions.length > 0 ? (
    <MrtTable
      initialState={{ columnVisibility: { 'mrt-row-expand': false } }}
      columns={[
        {
          id: 'time',
          header: $t({
            id: 'audit.props.time',
            defaultMessage: 'Hora',
          }),
          Cell: ({ row: { original: mention } }) => (
            <LTime value={mention.datetime} second={'numeric'} />
          ),
          size: 120,
        },
        {
          header: $t({
            id: 'audit.props.type',
            defaultMessage: 'Tipo',
          }),
          Cell: () => (
            <Chip type="primary" size="sm">
              {$t({
                id: 'audit.liveMention.badge',
                defaultMessage: 'Live Mention',
              })}
            </Chip>
          ),
          size: 120,
        },
        {
          header: $t({
            id: 'common.text.name',
            defaultMessage: 'Nombre',
          }),
          accessorKey: 'name',
          enableColumnFilter: false,
          size: 120,
        },
        {
          header: $t({
            id: 'common.text.description',
            defaultMessage: 'Descripción',
          }),
          accessorKey: 'description',
          enableColumnFilter: false,
          size: 200,
        },
        createStatusColumn<AuditLiveMention>($t, {
          size: 100,
          Cell: ({ row: { original: mention } }) => (
            <Chip
              size="sm"
              type={
                mention.confidence === 'ACCEPTED'
                  ? 'success'
                  : mention.confidence === 'REJECTED'
                    ? 'danger'
                    : 'light'
              }
            >
              <FormattedMessage
                id={`audit.liveMentions.confidence.${(mention.confidence ?? 'AUTO').toLowerCase()}`}
                defaultMessage={mention.confidence ?? 'AUTO'}
              />
            </Chip>
          ),
        }),
        createActionsColumn<AuditLiveMention>($t, {
          size: 200,
          Cell: ({ row: { original: mention, getIsExpanded, id }, table }) => (
            <div className="d-flex justify-content-around">
              <MentionPlayBtn liveMention={mention} />
              <MentionShowButton liveMention={mention} />
              <MentionConfidenceControls stationId={p.station.id} id={mention.id} className="m-0" />
              {mention.type === 'zeroshot' ? null : (
                <IconButton
                  size="small"
                  style={{ color: 'var(--grey)' }}
                  onClick={() => {
                    table.setExpanded({ [id]: !getIsExpanded() });
                  }}
                >
                  {getIsExpanded() ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
            </div>
          ),
        }),
      ]}
      data={mentions}
      renderDetailPanel={({ row }) => {
        return row.original.type === 'zeroshot' ? null : (
          <div>{String(row.original.customData?.content ?? '')}</div>
        );
      }}
      displayColumnDefOptions={{
        'mrt-row-expand': {
          enableHiding: false,
          visibleInShowHideMenu: false, //now row numbers are hidable too
        },
      }}
      enableExpandAll={false}
    />
  ) : (
    <ResultsCount count={0}>
      <FormattedMessage
        id="audit.liveMentionsScreen.noResults"
        defaultMessage="No se encontraron matches"
        values={{ count: 0 }}
      />
    </ResultsCount>
  );
}
